import { useCallback, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useLanguage } from '@/i18n/LanguageContext';
import type { RequestKind } from '@/api/types';

/** Where a request of this kind lives in the portal (the old /orders URLs redirect to /shipments). */
export const requestBase = (kind: RequestKind): string => (kind === 'OUTBOUND' ? '/shipments' : '/inbound');

/**
 * The seller's actions on a DRAFT: "send to work" and "delete". Both ask first and say what happens (a sent request
 * can no longer be deleted, only cancelled). Used by the lists and the detail pages so the wording and the rules
 * live in one place; the server enforces the same rules (a sent request answers 409 NOT_DRAFT).
 */
export function useDraftActions(kind: RequestKind, onChanged: (what: 'sent' | 'deleted', id: string) => void): {
  readonly busyId: string | null;
  readonly error: unknown;
  readonly clearError: () => void;
  readonly send: (id: string) => Promise<void>;
  readonly remove: (id: string) => Promise<void>;
} {
  const { t } = useLanguage();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const run = useCallback(
    async (id: string, what: 'sent' | 'deleted', fn: () => Promise<unknown>): Promise<void> => {
      setBusyId(id);
      setError(null);
      try {
        await fn();
        onChanged(what, id);
      } catch (e) {
        setError(e);
      } finally {
        setBusyId(null);
      }
    },
    [onChanged],
  );

  const send = useCallback(
    async (id: string): Promise<void> => {
      if (!window.confirm(t(kind === 'OUTBOUND' ? 'drafts.out.submitConfirm' : 'drafts.in.submitConfirm'))) return;
      await run(id, 'sent', () => (kind === 'OUTBOUND' ? endpoints.submitOutboundDraft(id) : endpoints.submitInboundDraft(id)));
    },
    [kind, run, t],
  );
  const remove = useCallback(
    async (id: string): Promise<void> => {
      if (!window.confirm(t('drafts.deleteConfirm'))) return;
      await run(id, 'deleted', () => (kind === 'OUTBOUND' ? endpoints.deleteOutboundDraft(id) : endpoints.deleteInboundDraft(id)));
    },
    [kind, run, t],
  );
  return { busyId, error, clearError: () => setError(null), send, remove };
}
